/**
 * WhatsCovered (PLAN-COVERAGE-TALKS, 34i91) — the Membership card's "What's
 * Covered" sub-tab, built to the Oct 21 mock. Three groups — Systems · Parts
 * the Plan Includes · Accessories & Indoor Air — every row carrying its STATE
 * as the fact (Covered · Included · Not Covered · Bulbs at the visit · Still on
 * File) and a LINK as the action. Clicking a not-included row opens the talk
 * card: Charge at the Visit · Add to the Plan from the Next Renewal · Add to the
 * Plan Starting Now (prorated) → Approve → a recorded plan adjustment → the next
 * ticket's Bring / Charge lines (the rail on the right shows the ticket as it
 * will read). The brain is planCoverage.ts; this file is the drawing.
 */
import { useMemo, useState } from "react";
import { Check, ChevronRight, RotateCcw, ShieldCheck, Undo2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/Button";
import type { ClassifiedEquip, EquipOverride, EquipmentView } from "@/lib/equipment";
import { installDateLabel } from "@/lib/equipment";
import { equipStyle } from "@/lib/equipmentIcons";
import { CopyCode } from "@/components/Copyable";
import { usePlanCatalog } from "@/features/agreements/PlanBuilder";
import { repairPctForTerm } from "@/features/agreements/planPricing";
import { usePlanDiscounts, useResolveFee } from "./serviceTypes";
import { useEditEquipmentFields, useSetEquipmentOverride } from "./equipmentOverrides";
import { PlanAccessoriesEditor, usePlanAccessories, type IncludedPart } from "./PlanAccessoriesEditor";
import {
  buildCoverage, findGhosts, prorate, useApproveAdjustment, useHomeNotes, usePlanAdjustments, useUndoAdjustment,
  money, longDate, type CoverRow, type CoverState, type PlanRef, type PriceOffer, type TicketPreview,
} from "@/features/agreements/planCoverage";

export interface WhatsCoveredProps {
  custno: string;
  locno: string | null;
  plan: PlanRef;
  termYears: number | null;
  termEnd: string | null;
  taskLabels: string[];
  nextCheck: { label: string; when: string } | null;
  includedParts: IncludedPart[];
  includedLoading?: boolean;
  equipView: EquipmentView;
  equipOverrides?: Map<string, EquipOverride>;
  /** who is on the ticket, for the preview — "Brent · call Corky on the way" */
  whoLine?: string | null;
  canEdit: boolean;
}

const STATE_STYLE: Record<CoverState, { label: string; cls: string }> = {
  covered: { label: "Covered", cls: "bg-emerald-50 text-emerald-700" },
  included: { label: "Included", cls: "bg-emerald-50 text-emerald-700" },
  added: { label: "Added", cls: "bg-emerald-50 text-emerald-700" },
  not_covered: { label: "Not Covered", cls: "bg-slate-100 text-slate-500" },
  chargeable: { label: "At the Visit", cls: "bg-amber-50 text-amber-700" },
  charge_approved: { label: "Charge at the Visit", cls: "bg-amber-50 text-amber-700" },
  ghost: { label: "Still on File", cls: "bg-rose-50 text-rose-700" },
  not_plan_item: { label: "Not a Plan Item", cls: "bg-slate-100 text-slate-500" },
};

function SectionLabel({ children, count, note }: { children: React.ReactNode; count?: number; note?: string }) {
  return (
    <div className="mb-2.5 flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-[.1em] text-slate-500">
      {children}
      {count != null && <span className="rounded-full bg-slate-100 px-1.5 py-px text-[10px] font-bold text-slate-500">{count}</span>}
      {note && <span className="text-[11px] font-medium normal-case tracking-normal text-slate-400">· {note}</span>}
    </div>
  );
}

export function WhatsCovered(p: WhatsCoveredProps) {
  const { data: catalogData } = usePlanCatalog();
  const { data: adjustments } = usePlanAdjustments(p.custno, p.locno);
  const { data: accessories } = usePlanAccessories(p.custno, p.locno);
  const { data: notes } = useHomeNotes(p.custno, p.locno);
  const approve = useApproveAdjustment();
  const undo = useUndoAdjustment();
  const setOverride = useSetEquipmentOverride();
  const editFields = useEditEquipmentFields();
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [kept, setKept] = useState<Set<string>>(new Set());
  const [showOld, setShowOld] = useState(false);

  const ghosts = useMemo(() => {
    const g = findGhosts(notes ?? [], [...p.equipView.auxiliary, ...p.equipView.systems]);
    for (const k of kept) g.delete(k);
    return g;
  }, [notes, p.equipView, kept]);

  const coverage = useMemo(() => buildCoverage({
    equipView: p.equipView,
    taskLabels: p.taskLabels,
    includedParts: p.includedParts,
    accessories: accessories ?? [],
    adjustments: adjustments ?? [],
    plan: p.plan,
    catalog: catalogData?.prices ?? [],
    termYears: p.termYears,
    termEnd: p.termEnd,
    ghosts,
    nextCheck: p.nextCheck,
    onPlanOverride: (counter) => {
      const ov = p.equipOverrides?.get(counter);
      return ov && ov.on_plan != null ? ov.on_plan : null;
    },
  }), [p.equipView, p.taskLabels, p.includedParts, accessories, adjustments, p.plan, catalogData, p.termYears, p.termEnd, ghosts, p.nextCheck, p.equipOverrides]);

  const onPlan = (e: ClassifiedEquip, on: boolean | null) =>
    editFields.mutate({ esc_counter: e.counter, custno: p.custno, locno: p.locno, on_plan: on });
  const markReplaced = (r: CoverRow) =>
    setOverride.mutate({
      esc_counter: r.equip.counter, custno: p.custno, locno: p.locno, status: "removed", removal_reason: "upgrade",
      removal_date: r.ghost?.date ?? null, note: r.ghost ? `Swapped for the ${r.ghost.by} — from the call notes${r.ghost.dispatch ? ` (call ${r.ghost.dispatch})` : ""}` : null,
    });

  const rowView = (r: CoverRow) => (
    <div key={r.key}>
      <CoverRowView
        row={r}
        open={openKey === r.key}
        canEdit={p.canEdit}
        onOpen={() => setOpenKey(openKey === r.key ? null : r.key)}
        onLeaveOff={() => onPlan(r.equip, false)}
        onMarkCovered={() => onPlan(r.equip, true)}
        onClearOverride={() => onPlan(r.equip, null)}
        onMarkReplaced={() => markReplaced(r)}
        onKeep={() => setKept((s) => new Set(s).add(r.key))}
        onUndo={r.adjustment ? () => undo.mutate({ adj: r.adjustment! }) : undefined}
        busy={approve.isPending || undo.isPending || setOverride.isPending || editFields.isPending}
      />
      {openKey === r.key && r.offer && p.canEdit && (
        <TalkCard
          row={r}
          offer={r.offer}
          termYears={p.termYears}
          termEnd={p.termEnd}
          planPrice={null}
          busy={approve.isPending}
          onClose={() => setOpenKey(null)}
          onApprove={(choice) => {
            const pr = prorate(r.offer!.amount, p.termYears, p.termEnd);
            approve.mutate({
              custno: p.custno, locno: p.locno, plan: p.plan,
              kind: choice === "visit" ? "charge_visit" : "add",
              catalogId: r.offer!.catalogId, label: r.offer!.label, equipCounter: r.equip.counter,
              amount: choice === "visit" ? (r.offer!.visitAmount ?? r.offer!.amount) : r.offer!.amount,
              effective: choice,
              proratedAmount: choice === "now" ? pr?.amount ?? null : null,
              effectiveDate: choice === "next_renewal" ? p.termEnd : choice === "now" ? new Date().toISOString().slice(0, 10) : null,
              saleLine: p.plan.kind === "pegasus" && choice === "now"
                ? { kind: r.offer!.catalogKind === "addon" ? "addon" : "item", id: r.offer!.catalogId, name: r.offer!.label, price: r.offer!.amount }
                : null,
            }, { onSuccess: () => setOpenKey(null) });
          }}
        />
      )}
    </div>
  );

  // Quiet by default: the rows read covered → chargeable → not covered → not a
  // plan item; unclassified "other" units (ESC's bare part numbers from 2010)
  // fold into one line so they do not crowd the systems that matter.
  const RANK: Record<CoverState, number> = { covered: 0, included: 0, added: 0, ghost: 1, chargeable: 2, charge_approved: 2, not_covered: 3, not_plan_item: 4 };
  const byState = (a: CoverRow, b: CoverRow) => RANK[a.state] - RANK[b.state];
  const systems = coverage.systems.filter((r) => r.equip.category !== "other").sort(byState);
  const others = coverage.systems.filter((r) => r.equip.category === "other");
  const accRows = [...coverage.accessories].sort(byState);
  const [showOthers, setShowOthers] = useState(false);
  return (
    <div className="space-y-6 px-6 py-5">
      <PlanGlance custno={p.custno} locno={p.locno} taskLabels={p.taskLabels} includedParts={p.includedParts} termYears={p.termYears} />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-6">
          {systems.length > 0 && (
            <div>
              <SectionLabel count={systems.length}>Systems</SectionLabel>
              <div className="space-y-1.5">{systems.map(rowView)}</div>
              {others.length > 0 && (
                <div className="mt-2">
                  <button type="button" onClick={() => setShowOthers((v) => !v)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 transition-colors hover:text-slate-600">
                    <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", showOthers && "rotate-90")} /> {others.length} other item{others.length === 1 ? "" : "s"} on file · not plan items
                  </button>
                  {showOthers && <div className="mt-2 space-y-1.5">{others.map(rowView)}</div>}
                </div>
              )}
            </div>
          )}
          <div>
            <SectionLabel count={coverage.parts.length || undefined} note={p.plan.kind === "esc" ? "from the ESC agreement · on every tune-up ticket" : "from the sale · on every tune-up ticket"}>Parts the Plan Includes</SectionLabel>
            <PlanAccessoriesEditor custno={p.custno} locno={p.locno} included={p.includedParts} includedLoading={p.includedLoading} embedded />
          </div>
          {accRows.length > 0 && (
            <div>
              <SectionLabel count={accRows.length}>Accessories &amp; Indoor Air</SectionLabel>
              <div className="space-y-1.5">{accRows.map(rowView)}</div>
            </div>
          )}
          {p.equipView.replaced.length > 0 && (
            <div>
              <button type="button" onClick={() => setShowOld((v) => !v)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 transition-colors hover:text-slate-700">
                <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", showOld && "rotate-90")} /> Previous / Older Equipment ({p.equipView.replaced.length})
              </button>
              {showOld && (
                <div className="mt-2 space-y-1.5">
                  {p.equipView.replaced.map((e) => (
                    <div key={e.counter} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/60 px-3.5 py-2 text-sm text-slate-500">
                      <span className="font-semibold text-slate-600">{e.model || e.mfg || e.categoryLabel}</span>
                      <span className="text-xs">{e.categoryLabel}{e.replacedDate ? ` · replaced ${longDate(e.replacedDate)}` : ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {coverage.systems.length === 0 && coverage.accessories.length === 0 && (
            <p className="text-sm text-slate-500">No equipment on file — add units in the Equipment tab.</p>
          )}
        </div>
        <TicketRail ticket={coverage.ticket} termYears={p.termYears} whoLine={p.whoLine} />
      </div>
    </div>
  );
}

// ─── a row ───────────────────────────────────────────────────────────────────
function CoverRowView({ row: r, open, canEdit, onOpen, onLeaveOff, onMarkCovered, onClearOverride, onMarkReplaced, onKeep, onUndo, busy }: {
  row: CoverRow; open: boolean; canEdit: boolean; busy: boolean;
  onOpen: () => void; onLeaveOff: () => void; onMarkCovered: () => void; onClearOverride: () => void;
  onMarkReplaced: () => void; onKeep: () => void; onUndo?: () => void;
}) {
  const e = r.equip;
  const st = STATE_STYLE[r.state];
  const style = equipStyle(e.category);
  const Icon = style.Icon;
  const title = e.model || e.mfg || e.categoryLabel;
  const sub = [e.categoryLabel, e.mfg && e.mfg !== title ? titleCase(e.mfg) : null].filter(Boolean).join(" · ");
  const undoable = !!r.adjustment && Date.now() - new Date(r.adjustment.approved_at).getTime() < 24 * 3600 * 1000;
  const offerWord = r.offer
    ? r.offer.catalogKind === "pad" ? "Add to Plan · no charge"
      : `Add to Plan · +${money(r.offer.amount)}${r.offer.catalogKind === "addon" ? " / term" : ""}`
    : null;
  const link = "text-[12.5px] font-bold text-brand-700 transition-colors hover:text-brand-800 disabled:opacity-50";
  const quiet = "text-[12.5px] font-semibold text-slate-400 transition-colors hover:text-slate-600 disabled:opacity-50";

  return (
    <div className={cn(
      "grid grid-cols-[36px_minmax(0,1fr)_auto] items-center gap-3 rounded-xl border bg-white px-3.5 py-2.5",
      r.state === "ghost" ? "border-rose-200 bg-rose-50/40" : "border-slate-100",
      open && "border-brand-500 ring-[3px] ring-brand-500/10",
    )}>
      <span className={cn("flex h-9 w-9 items-center justify-center rounded-[10px]", style.tile)}><Icon className="h-[18px] w-[18px]" /></span>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {e.model ? <CopyCode value={e.model} label="Copy model" variant="text" size="md" mono={false} className="font-bold text-slate-900" /> : <span className="text-sm font-bold text-slate-900">{title}</span>}
          {sub && <span className="text-[12.5px] text-slate-500">{sub}</span>}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-xs text-slate-400">
          {installDateLabel(e.install_date, "month") && <span>Installed {installDateLabel(e.install_date, "month")}</span>}
          {r.why && <span className={cn(r.state === "ghost" ? "font-semibold text-rose-700" : r.state === "chargeable" || r.state === "charge_approved" ? "text-amber-700" : "text-slate-500")}>{r.why}</span>}
        </div>
      </div>
      <div className="flex items-center justify-end gap-2.5">
        <span className={cn("inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-bold", st.cls)}>
          <span className="h-1.5 w-1.5 rounded-full bg-current" />
          {r.state === "chargeable" && r.offer ? `Bulbs · ${money(r.offer.visitAmount ?? r.offer.amount)} at the visit` : st.label}
        </span>
        {canEdit && r.state === "ghost" && <>
          <button type="button" disabled={busy} onClick={onMarkReplaced} className={link}>Mark Replaced</button>
          <button type="button" disabled={busy} onClick={onKeep} className={quiet}>Keep</button>
        </>}
        {canEdit && (r.state === "not_covered" || r.state === "chargeable") && r.offer && (
          <button type="button" disabled={busy} onClick={onOpen} className={link}>{offerWord}</button>
        )}
        {canEdit && r.state === "not_covered" && !r.offer && r.why !== "left off by the office" && (
          <button type="button" disabled={busy} onClick={onMarkCovered} className={quiet} title="Count this unit as covered by the plan">Mark Covered</button>
        )}
        {canEdit && r.state === "not_covered" && r.why === "left off by the office" && (
          <button type="button" disabled={busy} onClick={onClearOverride} className={quiet}>Put Back</button>
        )}
        {canEdit && r.state === "covered" && (
          <button type="button" disabled={busy} onClick={onLeaveOff} className={quiet} title="Leave this unit off the plan">Leave Off</button>
        )}
        {canEdit && (r.state === "added" || r.state === "charge_approved") && undoable && onUndo && (
          <button type="button" disabled={busy} onClick={onUndo} className={cn(quiet, "inline-flex items-center gap-1")}><Undo2 className="h-3.5 w-3.5" /> Undo</button>
        )}
      </div>
    </div>
  );
}

function titleCase(s: string): string {
  return s.length > 3 && s === s.toUpperCase() ? s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : s;
}

// ─── the talk card ───────────────────────────────────────────────────────────
type Choice = "visit" | "next_renewal" | "now";
function TalkCard({ row, offer, termYears, termEnd, busy, onApprove, onClose }: {
  row: CoverRow; offer: PriceOffer; termYears: number | null; termEnd: string | null; planPrice: number | null;
  busy: boolean; onApprove: (c: Choice) => void; onClose: () => void;
}) {
  const pr = prorate(offer.amount, termYears, termEnd);
  const isPad = offer.catalogKind === "pad";
  const canVisit = offer.catalogKind === "item" && offer.visitAmount != null;
  const [choice, setChoice] = useState<Choice>(isPad ? "now" : "next_renewal");
  const termWord = termYears ? (termYears === 1 ? "1 year" : `${termYears} years`) : "the term";
  const unit = titleCase(row.equip.model || row.equip.mfg || row.equip.categoryLabel);

  if (isPad) {
    return (
      <div className="mt-1.5 rounded-2xl border border-brand-200 bg-gradient-to-b from-brand-50/60 to-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[10.5px] font-bold uppercase tracking-[.1em] text-brand-700">{offer.label} · not on this plan</div>
            <div className="font-display mt-1 text-lg font-extrabold tracking-tight text-slate-900">Add the panel to the plan?</div>
            <div className="mt-0.5 text-[12.5px] text-slate-500">Water panels are included in every plan at no charge — it joins the Bring line of each fall check.</div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="md" disabled={busy} onClick={() => onApprove("now")}>Add · No Charge</Button>
            <Button size="md" variant="secondary" onClick={onClose}>Not Now</Button>
          </div>
        </div>
      </div>
    );
  }

  const opt = (c: Choice, title: string, meta: string, price: string) => (
    <button type="button" onClick={() => setChoice(c)} className={cn(
      "grid w-full grid-cols-[22px_minmax(0,1fr)_auto] items-center gap-3 rounded-xl border px-3.5 py-2.5 text-left text-[13.5px] transition-colors",
      choice === c ? "border-brand-500 bg-brand-50" : "border-slate-200 bg-white hover:border-slate-300",
    )}>
      <span className={cn("flex h-[18px] w-[18px] items-center justify-center rounded-full border-2", choice === c ? "border-brand-600" : "border-slate-300")}>
        {choice === c && <span className="h-2 w-2 rounded-full bg-brand-600" />}
      </span>
      <span><b className="font-bold text-slate-900">{title}</b><span className="mt-0.5 block text-[12.5px] text-slate-500">{meta}</span></span>
      <span className="whitespace-nowrap text-sm font-extrabold tabular-nums text-slate-900">{price}</span>
    </button>
  );

  return (
    <div className="mt-1.5 rounded-2xl border border-brand-200 bg-gradient-to-b from-brand-50/60 to-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10.5px] font-bold uppercase tracking-[.1em] text-brand-700">{offer.label} · not in this plan</div>
          <div className="font-display mt-1 text-lg font-extrabold tracking-tight text-slate-900">Add {offer.catalogKind === "addon" ? "it" : "them"} to the plan?</div>
          <div className="mt-0.5 text-[12.5px] text-slate-500">for the {unit}</div>
        </div>
        <div className="text-right">
          <div className="text-[10.5px] font-bold uppercase tracking-[.1em] text-slate-400">Plan price</div>
          <div className="font-display mt-0.5 text-lg font-extrabold tabular-nums tracking-tight text-slate-900">+{money(offer.amount)} <span className="text-xs font-semibold text-slate-500">for {termWord}</span></div>
        </div>
      </div>
      <div className="mt-3.5 grid gap-2">
        {canVisit && opt("visit", "Charge at the Visit", `${money(offer.visitAmount!)} on the next tune-up ticket · the plan stays as it is`, `${money(offer.visitAmount!)} once`)}
        {opt("next_renewal", "Add to the Plan · From the Next Renewal", termEnd ? `included from the renewal on ${longDate(termEnd)} · priced into it` : "included from the next renewal", `+${money(offer.amount)} / term`)}
        {pr && pr.left > 0 && opt("now", "Add to the Plan · Starting Now", `the rest of this term billed today · prorated ${pr.left} of ${pr.total} months`, `${money(pr.amount)} today`)}
      </div>
      <div className="mt-3.5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button size="md" disabled={busy} onClick={() => onApprove(choice)}>Approve · {choice === "visit" ? money(offer.visitAmount ?? offer.amount) : choice === "now" && pr ? money(pr.amount) : `+${money(offer.amount)}`}</Button>
          <Button size="md" variant="secondary" onClick={onClose}>Not Now</Button>
        </div>
        <span className="text-xs text-slate-500">recorded on the plan · undo for 24 hours</span>
      </div>
    </div>
  );
}

// ─── the rail: the next ticket + member benefits ─────────────────────────────
function TicketRail({ ticket, whoLine }: { ticket: TicketPreview; termYears: number | null; whoLine?: string | null }) {
  const chips = (lines: TicketPreview["bring"]) => lines.map((l) => <span key={l.label} className="rounded-md border border-slate-200 bg-white px-1.5 py-0.5 font-mono text-[11px] font-bold text-slate-700">{l.label}{l.qty && l.qty > 1 ? ` ×${l.qty}` : ""}</span>);
  return (
    <div className="space-y-6">
      <div>
        <SectionLabel>The Next Tune-Up Ticket</SectionLabel>
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3.5">
          <div className="rounded-xl border-l-4 border-brand-600 bg-white px-3.5 py-2.5 shadow-sm">
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
              <span className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 font-bold text-emerald-700"><ShieldCheck className="h-3 w-3" /> Plus</span>
              <span className="text-slate-400">{ticket.when ?? "next check"}</span>
            </div>
            <div className="mt-1 text-sm font-extrabold text-slate-900">{ticket.check ?? "Plan tune-up"}</div>
          </div>
          <div className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-[12.5px]">
            <span className="pt-0.5 text-[10px] font-bold uppercase tracking-[.1em] text-slate-400">Bring</span>
            <span className="flex flex-wrap items-center gap-1">{ticket.bring.length ? <>{chips(ticket.bring)}<span className="text-slate-500">· included</span></> : <span className="text-slate-400">nothing on the plan yet</span>}</span>
            {ticket.charge.length > 0 && <>
              <span className="pt-0.5 text-[10px] font-bold uppercase tracking-[.1em] text-slate-400">Charge</span>
              <span className="font-semibold text-amber-700">{ticket.charge.map((c) => `${c.label} · ${money(c.amount ?? 0)}`).join(" · ")}</span>
            </>}
            {ticket.offer.length > 0 && <>
              <span className="pt-0.5 text-[10px] font-bold uppercase tracking-[.1em] text-slate-400">Offer</span>
              <span className="text-amber-700">{ticket.offer.map((c) => `${c.label} · ${money(c.amount ?? 0)}`).join(" · ")} <span className="text-slate-400">· until approved</span></span>
            </>}
            {whoLine && <>
              <span className="pt-0.5 text-[10px] font-bold uppercase tracking-[.1em] text-slate-400">Who</span>
              <span className="text-slate-700">{whoLine}</span>
            </>}
          </div>
          <div className="mt-3 border-t border-slate-200 pt-2.5 text-[11.5px] text-slate-500">Composed from What's Covered · the same lines Parts for the Day reads</div>
        </div>
      </div>
    </div>
  );
}

/**
 * PLAN-GLANCE (Seth 2026-10-21: "seeing from a high level what is included in
 * the plan feels like there is room for improvement"): one quiet band — the
 * checks, the parts, the perks — in chips, no sentences. Lives on the Overview
 * and at the top of What's Covered; the rows below it are the detail.
 */
export function PlanGlance({ custno, locno, taskLabels, includedParts, termYears, compact }: {
  custno: string; locno: string | null; taskLabels: string[]; includedParts: IncludedPart[]; termYears: number | null; compact?: boolean;
}) {
  const { data: accessories } = usePlanAccessories(custno, locno);
  const { data: tiers } = usePlanDiscounts();
  const { data: memberFee } = useResolveFee("frn_ac_diag", { plus: true });
  const { data: standardFee } = useResolveFee("frn_ac_diag", { plus: false });
  const repair = repairPctForTerm(tiers, termYears ?? 1);
  const saves = memberFee && standardFee ? Math.max(0, Number(standardFee.amount) - Number(memberFee.amount)) : 0;
  const checks: string[] = [];
  const has = (re: RegExp) => taskLabels.some((l) => re.test(l));
  if (has(/^Furnace/)) checks.push("Furnace Check · each fall");
  if (has(/^Boiler/)) checks.push("Boiler Check · each fall");
  if (has(/^A\/C/)) checks.push("A/C Check · each spring");
  if (has(/^Mini/)) checks.push("Mini-Split Check · each spring");
  if (has(/^Plumbing/)) checks.push("Plumbing Check · yearly");
  const seen = new Set<string>();
  const parts: { code: string; name: string | null }[] = [];
  const shortName = (d: string | null) => {
    if (!d) return null;
    const m = d.match(/#\s?(\d+)/);
    if (/water panel|pad/i.test(d)) return m ? `#${m[1]} panel` : "water panel";
    if (/media/i.test(d)) return m ? `#${m[1]} media` : "media";
    if (/filter/i.test(d)) { const sz = d.match(/\d{1,2}\s?X\s?\d{1,2}(?:\s?X\s?\d)?/i); return sz ? `${sz[0].replace(/\s/g, "").toLowerCase()} filter` : "filter"; }
    if (/bulb/i.test(d)) return "bulbs";
    return d.length > 26 ? d.slice(0, 24) + "…" : d;
  };
  for (const p of includedParts) { const k = p.code.toUpperCase(); if (seen.has(k)) continue; seen.add(k); parts.push({ code: p.code, name: shortName(p.description) }); }
  for (const a of accessories ?? []) { const k = a.label.toUpperCase(); if (seen.has(k)) continue; seen.add(k); parts.push({ code: a.label, name: null }); }
  const perks: string[] = [];
  if (repair > 0) perks.push(`${Math.round(repair * 100)}% off repairs`);
  if (memberFee) perks.push(`${money(Number(memberFee.amount))} diagnostic${saves > 0 ? ` · saves ${money(saves)}` : ""}`);
  perks.push("Priority scheduling");
  const chip = "inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[12.5px] font-semibold text-slate-700";
  const col = (label: string, items: React.ReactNode[], empty: string) => (
    <div className="min-w-0">
      <div className="mb-2 text-[10.5px] font-bold uppercase tracking-[.1em] text-slate-400">{label}</div>
      <div className="flex flex-wrap gap-1.5">{items.length ? items : <span className="text-[12.5px] text-slate-400">{empty}</span>}</div>
    </div>
  );
  return (
    <div className={cn("grid gap-5 rounded-2xl border border-slate-200 bg-slate-50/70 p-4", compact ? "sm:grid-cols-3" : "sm:grid-cols-3")}>
      {col("Tune-Ups", checks.map((c) => <span key={c} className={chip}><Check className="h-3.5 w-3.5 text-emerald-600" />{c}</span>), "none on the plan")}
      {col("Parts Included", parts.map((p) => <span key={p.code} className={chip}><span className="font-mono text-[11.5px] font-bold text-slate-900">{p.code}</span>{p.name && <span className="text-slate-500">{p.name}</span>}</span>), "no parts on the plan")}
      {col("Member Perks", perks.map((c) => <span key={c} className={chip}><Check className="h-3.5 w-3.5 text-emerald-600" />{c}</span>), "—")}
    </div>
  );
}

/** The Overview's Plan Adjustments ledger — what the office approved, with Undo inside 24 hours. */
export function PlanAdjustmentsLedger({ custno, locno, plan, renewals, planAmount }: {
  custno: string; locno: string | null; plan: PlanRef;
  renewals?: { date: string; amount: number }[];
  /** the term price — an ESC invoice near it reads "Renewed", anything smaller "Charge" */
  planAmount?: number | null;
}) {
  const { data: adjustments } = usePlanAdjustments(custno, locno);
  const undo = useUndoAdjustment();
  const mine = (adjustments ?? []).filter((a) => (plan.kind === "esc" ? a.agrmt_no === plan.agrmtNo : a.plan_sale_id === plan.saleId));
  type Row = { key: string; date: string; what: React.ReactNode; amount: string; who: string; adj?: typeof mine[number] };
  const rows: Row[] = [
    ...mine.map((a) => ({
      key: a.id, date: a.approved_at.slice(0, 10),
      what: <><b className="font-bold text-slate-900">{a.label}</b> {a.kind === "charge_visit" ? "· charge at the visit" : a.effective === "now" ? "· added · starting now" : `· added · from the ${a.effective_date ? longDate(a.effective_date) : "next"} renewal`}</>,
      amount: a.kind === "charge_visit" ? money(a.amount) : `+${money(a.amount)}`,
      who: (a.approved_by ?? "").split("@")[0] || "—", adj: a,
    })),
    ...(renewals ?? []).map((r) => {
      const renewal = !planAmount || Math.abs(r.amount - planAmount) <= planAmount * 0.25;
      return { key: `r${r.date}`, date: r.date, what: <><b className="font-bold text-slate-900">{renewal ? "Renewed" : "Charge"}</b>{renewal ? "" : " · on the agreement"}</>, amount: money(r.amount), who: "ESC" };
    }),
  ].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 8);
  if (rows.length === 0) return null;
  return (
    <div>
      <SectionLabel count={mine.length || undefined}>Plan Adjustments</SectionLabel>
      <div className="grid grid-cols-[96px_minmax(0,1fr)_auto_auto] items-center gap-x-3.5 gap-y-2 text-[13px]">
        {rows.map((r) => {
          const undoable = r.adj && Date.now() - new Date(r.adj.approved_at).getTime() < 24 * 3600 * 1000;
          return (
            <div key={r.key} className="contents">
              <span className="tabular-nums text-slate-500">{longDate(r.date)}</span>
              <span className="min-w-0 text-slate-700">{r.what}</span>
              <span className={cn("tabular-nums font-bold", r.adj && r.adj.kind !== "charge_visit" ? "text-emerald-700" : "text-slate-900")}>{r.amount}</span>
              <span className="flex items-center gap-2 text-xs text-slate-400">
                {r.who}
                {undoable && <button type="button" onClick={() => undo.mutate({ adj: r.adj! })} disabled={undo.isPending} className="inline-flex items-center gap-1 font-semibold text-slate-400 hover:text-slate-700 disabled:opacity-50"><RotateCcw className="h-3 w-3" /> Undo</button>}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-3 text-xs text-slate-400">{plan.kind === "pegasus" ? "A change starting now updates the sale itself · from the next renewal, the renewal is priced from it" : "Recorded in Pegasus · the renewal is priced from it · the money reaches ESC with the invoice"}</div>
    </div>
  );
}

